import moment from 'moment'
import store from '@/store'
import firebase from 'firebase'
import router from '@/router'

import BehaviorReport from '@/components/BehaviorReport'
import SupportPlan from '@/components/SupportPlan'
import ProgressMonitor from "@/components/ProgressMonitor"
import ChartDog from "@/components/ChartDog"
import ChecklistMaker from "@/components/ChecklistMaker"
import ListGenerator from "@/components/ListGenerator"

// all the available tools
const TOOLS = {
    chartDog: ChartDog,
    progressMonitor: ProgressMonitor,
    behaviorReport: BehaviorReport,
    supportPlan: SupportPlan,
    checklistMaker: ChecklistMaker,
    listGenerator: ListGenerator,
}

function saveDoc() {
    const doc = store.state.doc

    getUserDocs()
        .child(store.state.docId)
        .update({
            updated: moment().valueOf(),
            title: doc.title,
            data: doc.data,
            // include these, as they can be omitted if a formerly anonymous doc gets
            // copied to the user's docs on sign-in
            created: doc.created,
            tool: doc.tool
        })

    updateWindowTitle(doc)
}

function saveConfig() {
    firebase.database().ref()
        .child('config')
        .update(store.state.config)
}

function updateWindowTitle(doc) {
    if (doc && TOOLS[doc.tool]) {
        window.document.title = `${doc.title} - ${TOOLS[doc.tool].options.label}`
    }
}

function getUserDocs() {
    if (store.state.currentUserEmail) {
        // console.log('getUserDocs', store.state.currentUserEmail)
        return firebase.database().ref()
            .child('users')
            .child(store.getters.currentUserKey)
            .child('docs')
    } else {
        // console.log('getUserDocs anonymous')
        return firebase.database().ref()
            .child('anonymousDocs')
    }
}

// --------------------------------------------

const api = {

    TOOLS: TOOLS,

    DEFAULT_DOC_TITLE: 'Add Title',

    initFirebase() {
        firebase.auth().onAuthStateChanged((firebaseUser) => {
            console.log('onAuthStateChanged, user', firebaseUser)
            if (firebaseUser) {
                if (firebaseUser.email) {
                    store.state.currentUserEmail = firebaseUser.email
                }
            }

            this.ensureUserNodeExists()
            this.checkIsAdmin()
            this.ensureDocLoaded()
            this.ensureDocsLoaded()
            this.checkForSignInLink()
        });
    },

    // ensure that a node exists for this user under 'users' (this is used to store their docs)
    ensureUserNodeExists() {
        const key = store.getters.currentUserKey
        if (key) {
            var userObj = firebase.database().ref().child('users').child(key)
            userObj.update({email: store.state.currentUserEmail})
        }
    },

    checkIsAdmin() {
        if (store.getters.currentUserKey) {
            firebase.database().ref()
                .child('users')
                .child(store.getters.currentUserKey)
                .child('is_admin')
                .once("value")
                .then(snapshot => {
                    const is = snapshot.val()
                    if (is) {
                        store.state.userIsAdmin = true
                    }
                })
        }
    },

    // the loadDoc action will fail if the fb user is not set yet (as when loading a doc-edit page
    // directly by URL) - so we call this to try again once the user is set
    ensureDocLoaded() {
        if (store.state.docId && !store.state.doc) {
            store.dispatch('loadDoc', {docId: store.state.docId})
        }
    },

    // if loadDocs is called before the fb user is set, it will load the anonymous docs,
    // so we call this after user changes
    ensureDocsLoaded() {
        if (store.state.currentUserEmail) {
            store.dispatch('loadDocs')
        }
    },

    checkForSignInLink() {
        // Confirm the link is a sign-in with email link.
        if (firebase.auth().isSignInWithEmailLink(window.location.href)) {

            // Get the email if available. This should be available if the user completes
            // the flow on the same device where they started it.
            var email = window.localStorage.getItem('emailForSignIn');
            if (!email) {
                // User opened the link on a different device. To prevent session fixation
                // attacks, ask the user to provide the associated email again. For example:
                email = window.prompt('Please provide your email for confirmation');
            }

            // The client SDK will parse the code from the link for you.
            firebase.auth().signInWithEmailLink(email, window.location.href)
                .then(function (result) {
                    // NB: don't remove 'emailForSignIn' from localstorage, to prevent spurious confirmation popups...
                    window.location.search = '';
                })
                .catch(function (error) {
                });
            return true;
        } else {
            return false;
        }
    },

    signIn(email) {
        const auth = firebase.auth()
        const actionCodeSettings = {
            url: window.location.href,
            handleCodeInApp: true
        }

        auth.sendSignInLinkToEmail(email, actionCodeSettings)
            .then(function () {
                window.localStorage.setItem('emailForSignIn', email)
            })
            .catch(function (error) {
                console.log("Couldn't send sign in link", error)
            });
    },

    signOut() {
        firebase.auth().signOut();
        store.state.currentUserEmail = null
        store.state.userIsAdmin = false
        router.replace('/')
    },

    loadConfig() {
        firebase.database().ref()
            .child('config')
            .once("value")
            .then(snapshot => {
                store.state.config = snapshot.val()
            })
    },

    loadDocs() {
        console.log('loadDocs')
        if (store.state.currentUserEmail) {
            getUserDocs()
                .once("value")
                .then(snapshot => {
                    store.state.docs = snapshot.val()
                    // console.log(store.state.docs)
                })
        } else {
            store.state.docs = []
        }
    },

    loadDoc(docId) {
        // important: set this for use by ensureDocLoaded()
        store.state.docId = docId

        // TODO: handle onDataLoading/onDataLoaded
        getUserDocs()
            .child(docId)
            .once("value")
            .then(snapshot => {
                const doc = snapshot.val()
                if (doc) {
                    store.state.docNotFound = false
                    store.state.doc = doc
                    store.state.tool = TOOLS[doc.tool]
                    updateWindowTitle(doc)
                } else {
                    store.state.docNotFound = true
                }
            })
    },

    cloneDoc(doc) {
        getUserDocs()
            .push({
                created: moment().valueOf(),
                updated: moment().valueOf(),
                title: 'Copy of ' + doc.data.docTitle,
                tool: doc.tool,
                data: Object.defineProperty(doc.data, 'createdForms', {value: [], configurable: true, writable: true})
            })
            .then((d) => {
                console.log('saved cloned doc', d.key)
                this.loadDocs()
            });
    },

    // tool is e.g. SupportPlan
    createNewDoc(tool) {
        return getUserDocs()
            .push({
                created: moment().valueOf(),
                updated: moment().valueOf(),
                title: this.DEFAULT_DOC_TITLE,
                tool: tool.options.toolName,
                data: tool.options.getDefaultData()
            })
    },

    deleteDoc(docId) {
        if (confirm('Permanently delete this document?\nThere is no undo for this action!')) {
            return getUserDocs()
                .child(docId)
                .remove()
                .then(() => {
                    // console.log('doc removed')
                    this.loadDocs()
                });
        }
    },

    deleteDocWithoutConfirm(docId) {
        return getUserDocs()
            .child(docId)
            .remove()
            .then(() => {
                // console.log('doc removed')
                this.loadDocs()
            });
    },

    debouncedSaveDoc: _.debounce(saveDoc, 1000),

    debouncedSaveConfig: _.debounce(saveConfig, 1000),
}

export default api